import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useHouseholds, queryKeys, type HouseholdWithMembers } from '@pantry/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';

const STORAGE_KEY = 'pantry_selected_household_id';

interface HouseholdContextType {
  selectedHousehold: HouseholdWithMembers | null;
  households: HouseholdWithMembers[];
  isLoading: boolean;
  selectHousehold: (householdId: string) => void;
}

const HouseholdContext = createContext<HouseholdContextType | null>(null);

export function HouseholdProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: households = [], isLoading } = useHouseholds();
  const [selectedHouseholdId, setSelectedHouseholdId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY);
    }
    return null;
  });

  // Track the previous user ID to detect user changes
  const previousUserIdRef = useRef<string | undefined>(user?.id);

  // Reset household selection and refetch data when user changes (logout/login as different user)
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    const currentUserId = user?.id;

    // Only reset if the user actually changed (not on initial mount)
    if (previousUserId !== undefined && previousUserId !== currentUserId) {
      // User changed - clear the selected household
      setSelectedHouseholdId(null);
      localStorage.removeItem(STORAGE_KEY);

      // Invalidate households query to force refetch with new user's data
      queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
    }

    // Update the ref for next comparison
    previousUserIdRef.current = currentUserId;
  }, [user?.id, queryClient]);

  // Find the selected household from the list
  const selectedHousehold = households.find((h) => h.id === selectedHouseholdId) ?? null;

  // Auto-select first household if none selected and households are loaded
  useEffect(() => {
    if (!isLoading && households.length > 0 && !selectedHousehold) {
      const firstHouseholdId = households[0].id;
      setSelectedHouseholdId(firstHouseholdId);
      localStorage.setItem(STORAGE_KEY, firstHouseholdId);
    }
  }, [isLoading, households, selectedHousehold]);

  // Clear selection if the selected household no longer exists
  useEffect(() => {
    if (
      !isLoading &&
      selectedHouseholdId &&
      households.length > 0 &&
      !households.find((h) => h.id === selectedHouseholdId)
    ) {
      // Selected household was deleted, clear selection
      setSelectedHouseholdId(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isLoading, households, selectedHouseholdId]);

  const selectHousehold = useCallback((householdId: string) => {
    setSelectedHouseholdId(householdId);
    localStorage.setItem(STORAGE_KEY, householdId);
  }, []);

  return (
    <HouseholdContext.Provider
      value={{
        selectedHousehold,
        households,
        isLoading,
        selectHousehold,
      }}
    >
      {children}
    </HouseholdContext.Provider>
  );
}

export function useSelectedHousehold() {
  const context = useContext(HouseholdContext);
  if (!context) {
    throw new Error('useSelectedHousehold must be used within a HouseholdProvider');
  }
  return context;
}
